/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'sharedUi/Button';
import { valueSetsService } from '../../services/value-sets.service';
import type { Recipient } from '../../services/notification-masters.service';

/**
 * Edits a notification's recipients.
 *
 * Each entry describes one audience and how it is treated HERE, not the
 * recipient itself — which is what lets the same recipient be the main audience
 * of one notification and a fallback on another without being copied.
 */
interface RecipientEditorProps {
  value: Recipient[];
  onChange: (next: Recipient[]) => void;
  recipientOptions: Array<{ value: string; label: string }>;
  testId?: string;
}

const BLANK: Recipient = {
  recipientMasterId: '',
  isMandatory: false,
  selection: { mode: 'ALL' },
  fallback: { enabled: false, chain: [] },
};

const RecipientEditor = ({
  value, onChange, recipientOptions, testId = 'RecipientEditor',
}: RecipientEditorProps) => {
  const { data: modeVS } = useQuery({
    queryKey: ['vs', 'selectionMode'],
    queryFn: () => valueSetsService.getByKey('selectionMode', 'en'),
    staleTime: 3e5,
  });
  const opts = (vs: any) => (vs?.items || []).map((i: any) => ({ value: i.code, label: i.label }));

  const update = (index: number, next: Recipient) =>
    onChange(value.map((r, i) => (i === index ? next : r)));

  // A recipient already used cannot be picked again: `recipientMasterId` IS the
  // entry's identity, so a duplicate makes two entries indistinguishable and the
  // API refuses the save.
  const availableFor = (index: number) => {
    const taken = new Set(value.filter((_, i) => i !== index).map((r) => r.recipientMasterId));
    return recipientOptions.filter((o) => !taken.has(o.value));
  };

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Recipients</label>
        <span className="block text-xs text-gray-400">
          Who this notification goes to, and how each audience is treated. A recipient may appear at most once.
        </span>
      </div>

      {value.length === 0 && (
        <p className="text-xs text-gray-400">
          None yet — a notification with no recipients resolves nobody and delivers nothing.
        </p>
      )}

      {value.map((entry, index) => (
        <div key={index} className="border border-line rounded p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Dropdown
              value={entry.recipientMasterId}
              options={availableFor(index)}
              optionLabel="label"
              optionValue="value"
              placeholder="Recipient"
              className="w-56"
              data-testid={`${testId}.Recipient.${index}`}
              onChange={(e) => update(index, { ...entry, recipientMasterId: e.value })}
            />
            <div className="flex items-center gap-1.5">
              <Checkbox
                inputId={`${testId}-mandatory-${index}`}
                checked={!!entry.isMandatory}
                onChange={(e) => update(index, { ...entry, isMandatory: !!e.checked })}
              />
              <label htmlFor={`${testId}-mandatory-${index}`} className="text-xs text-gray-700 dark:text-gray-300">
                Mandatory
              </label>
            </div>
            <button aria-label="Remove recipient"
              type="button"
              title="Remove recipient"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ml-auto"
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <i className="pi pi-trash text-red-600 dark:text-red-400" />
            </button>
          </div>

          {entry.isMandatory && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              If this audience resolves to nobody, the whole send fails.
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Dropdown
              value={entry.selection?.mode || 'ALL'}
              options={opts(modeVS)}
              optionLabel="label"
              optionValue="value"
              className="w-32"
              data-testid={`${testId}.Mode.${index}`}
              onChange={(e) => update(index, { ...entry, selection: { ...entry.selection, mode: e.value } })}
            />
            {entry.selection?.mode === 'TOP_N' && (
              <>
                <InputNumber
                  value={entry.selection?.topN ?? null}
                  min={1}
                  placeholder="topN"
                  className="w-24"
                  data-testid={`${testId}.TopN.${index}`}
                  onValueChange={(e) => update(index, { ...entry, selection: { ...entry.selection, topN: e.value ?? null } })}
                />
                <InputText
                  value={entry.selection?.rankingExpression || ''}
                  placeholder="seniority DESC"
                  className="w-48 text-sm"
                  data-testid={`${testId}.Ranking.${index}`}
                  onChange={(e) => update(index, { ...entry, selection: { ...entry.selection, rankingExpression: e.target.value } })}
                />
              </>
            )}
          </div>

          {entry.selection?.mode === 'TOP_N' && (
            <p className="text-xs text-gray-400">
              A count is required, or this reaches nobody. Rank by any attribute the chain returns,
              or leave it empty to take them in the order it gives.
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Checkbox
                inputId={`${testId}-fallback-${index}`}
                checked={!!entry.fallback?.enabled}
                onChange={(e) => update(index, { ...entry, fallback: { ...entry.fallback, enabled: !!e.checked } })}
              />
              <label htmlFor={`${testId}-fallback-${index}`} className="text-xs text-gray-700 dark:text-gray-300">
                Fallback when it resolves to nobody
              </label>
            </div>
            {entry.fallback?.enabled && (
              <InputText
                value={(entry.fallback?.chain || []).join(', ')}
                placeholder="recipient ids, tried in order"
                className="flex-1 text-sm font-mono"
                data-testid={`${testId}.Chain.${index}`}
                onChange={(e) => update(index, {
                  ...entry,
                  fallback: {
                    ...entry.fallback,
                    chain: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  },
                })}
              />
            )}
          </div>
        </div>
      ))}

      <div>
        <Button type="button" label="Add recipient" icon="pi pi-plus" size="small" severity="secondary" outlined
          onClick={() => onChange([...value, { ...BLANK }])} data-testid={`${testId}.Add`} />
      </div>
    </div>
  );
};

export default RecipientEditor;
